import { Controller } from "@hotwired/stimulus"
import { application } from "../stimulus_application"

const PLACEMENT_PREVIEW_IDENTIFIERS = [
  "admin--apple-card-preview-mapping",
  "admin--google-card-preview-mapping"
]

export default class extends Controller {
  static targets = [
    "readerSlug", "fieldRows", "template", "hiddenJson",
    "previewBody", "previewSection", "mappedPreviewBody", "jsonPreview",
    "valueFilterMode", "valueFilterPairs"
  ]

  static values = {
    sampleData: { type: Array, default: [] },
    readerConfig: { type: Object, default: {} }
  }

  connect() {
    this._placementTimer = null
    this.renderReaderPreview()
    this.updatePathDropdowns()
    this.syncJson()
    this.renderMappedPreview()
  }

  disconnect() {
    if (this._placementTimer != null) {
      clearTimeout(this._placementTimer)
      this._placementTimer = null
    }
  }

  addRow() {
    if (!this.hasTemplateTarget || !this.hasFieldRowsTarget) return
    const clone = this.templateTarget.content.cloneNode(true)
    this.fieldRowsTarget.appendChild(clone)
    this.updatePathDropdowns()
    this.syncJson()
  }

  removeRow(event) {
    const row = event.currentTarget.closest("[data-field-row]")
    if (!row || row.dataset.required) return
    const group = row.closest("tbody.field-mapping-group")
    if (group) group.remove()
    this.syncJson()
  }

  addJoinPath(event) {
    const btn = event.currentTarget
    const row = btn.closest("[data-field-row]")
    const container = row?.querySelector("[data-join-paths]")
    if (!container) return

    const pathItems = container.querySelectorAll("[data-join-path-item]")
    if (pathItems.length === 1) {
      const joinSelect = document.createElement("select")
      joinSelect.className = "form-control form-control-sm"
      joinSelect.style.width = "auto"
      joinSelect.style.minWidth = "4rem"
      joinSelect.dataset.field = "join"
      joinSelect.dataset.action = "change->admin--field-mapping-editor#fieldChanged"
      joinSelect.innerHTML = `
        <option value=",">Comma</option>
        <option value=":">Colon</option>
        <option value=".">Dot</option>
        <option value="-">Dash</option>
        <option value=" " selected>Space</option>
      `
      container.appendChild(joinSelect)
    }

    const span = document.createElement("span")
    span.className = "d-inline-flex align-items-center gap-2"
    span.dataset.joinPathItem = ""
    span.innerHTML = `
      <select class="form-control form-control-sm" style="min-width:10rem" data-field="path" data-action="change->admin--field-mapping-editor#fieldChanged">
        <option value="">— select —</option>
      </select>
      <button class="btn btn-danger btn-sm" type="button" data-action="click->admin--field-mapping-editor#removeJoinPath" title="Remove field" aria-label="Remove field">
        <i class="fas fa-times"></i>
      </button>
    `
    container.appendChild(span)
    this.updatePathDropdowns()
    this.syncJson()
    this.renderMappedPreview()
  }

  removeJoinPath(event) {
    const btn = event.currentTarget
    const item = btn.closest("[data-join-path-item]")
    if (!item) return
    const container = item.closest("[data-join-paths]")
    const items = container?.querySelectorAll("[data-join-path-item]") || []
    if (items.length <= 1) return

    item.remove()
    if (container.querySelectorAll("[data-join-path-item]").length === 1) {
      const joinSelect = container.querySelector("[data-field='join']")
      if (joinSelect) joinSelect.remove()
    }
    this.updatePathDropdowns()
    this.syncJson()
    this.renderMappedPreview()
  }

  addMapRule(event) {
    const btn = event.currentTarget
    const tbody = btn.closest("[data-map-row]")?.querySelector("[data-map-rules]")
    if (!tbody) return

    const tr = document.createElement("tr")
    tr.dataset.mapRule = ""
    tr.innerHTML = `
      <td>
        <input class="form-control form-control-sm" type="text" data-map-input="" placeholder="e.g. 8" data-action="input->admin--field-mapping-editor#fieldChanged">
      </td>
      <td>
        <input class="form-control form-control-sm" type="text" data-map-output="" placeholder="e.g. Individual" data-action="input->admin--field-mapping-editor#fieldChanged">
      </td>
      <td>
        <button class="btn btn-danger btn-sm" type="button" data-action="click->admin--field-mapping-editor#removeMapRule" aria-label="Remove">
          <i class="fas fa-times"></i>
        </button>
      </td>
    `
    tbody.appendChild(tr)
    this.syncJson()
  }

  removeMapRule(event) {
    const row = event.currentTarget.closest("[data-map-rule]")
    if (row) row.remove()
    this.syncJson()
  }

  fieldChanged(event) {
    const target = event?.target
    if (target?.dataset?.field === "key") {
      const sanitized = this.sanitizeFieldKey(target.value)
      if (target.value !== sanitized) target.value = sanitized
    }
    this.syncJson()
    this.renderMappedPreview()
  }

  sanitizeFieldKey(value) {
    const v = String(value ?? "").toLowerCase().replace(/[^a-z0-9_-]/g, "")
    // Must start with a-z.
    return v.replace(/^[^a-z]+/, "")
  }

  typeChanged(event) {
    const row = event.currentTarget.closest("[data-field-row]")
    const type = event.currentTarget.value
    const simpleTypes = ["text", "date", "email"]
    const contentKey = simpleTypes.includes(type) ? "simple" : type

    row.querySelectorAll("[data-type-content]").forEach((el) => {
      el.classList.toggle("d-none", el.dataset.typeContent !== contentKey)
    })

    let next = row.nextElementSibling
    while (next && (next.dataset.statusMapRow !== undefined || next.dataset.mapRow !== undefined)) {
      const isStatus = next.dataset.statusMapRow !== undefined && type === "status"
      const isMap = next.dataset.mapRow !== undefined && type === "map"
      next.classList.toggle("d-none", !isStatus && !isMap)
      next = next.nextElementSibling
    }

    this.updatePathDropdowns()
    this.syncJson()
    this.renderMappedPreview()
  }

  addStatusRule(event) {
    const btn = event.currentTarget
    const tbody = btn.closest("[data-status-map-row]")?.querySelector("[data-status-rules]")
    if (!tbody) return

    const tr = document.createElement("tr")
    tr.dataset.statusRule = ""
    tr.innerHTML = `
      <td>
        <input class="form-control form-control-sm" type="text" data-status-input="" placeholder="e.g. active, cancelled" data-action="input->admin--field-mapping-editor#fieldChanged">
      </td>
      <td>
        <select class="form-control form-control-sm" data-status-output="" data-action="change->admin--field-mapping-editor#fieldChanged">
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Expired">Expired</option>
        </select>
      </td>
      <td>
        <button class="btn btn-danger btn-sm" type="button" data-action="click->admin--field-mapping-editor#removeStatusRule" aria-label="Remove">
          <i class="fas fa-times"></i>
        </button>
      </td>
    `
    tbody.appendChild(tr)
    this.syncJson()
  }

  removeStatusRule(event) {
    const btn = event.currentTarget
    const row = btn.closest("[data-status-rule]")
    if (row) row.remove()
    this.syncJson()
  }

  renderReaderPreview() {
    if (!this.hasPreviewBodyTarget || !this.sampleDataValue.length) return

    const sample = this.sampleDataValue[0]
    const rows = this.extractLeafPathsWithValues(sample, "")

    if (rows.length === 0) {
      this.previewBodyTarget.innerHTML =
        "<tr><td colspan=\"2\" class=\"text-muted small\">No sample rows. Configure your data source first.</td></tr>"
      return
    }

    const html = rows
      .map(
        ([path, value]) =>
          `<tr><td class="small">${this.escapeHtml(this.inputFieldLabel(path))}</td><td class="small">${this.escapeHtml(value)}</td></tr>`
      )
      .join("")
    this.previewBodyTarget.innerHTML = html
  }

  extractLeafPathsWithValues(obj, prefix) {
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) return []

    return Object.entries(obj).flatMap(([key, value]) => {
      const path = prefix ? `${prefix}.${key}` : key
      if (value && typeof value === "object" && !Array.isArray(value)) {
        return this.extractLeafPathsWithValues(value, path)
      }
      return [[path, value != null ? String(value) : "—"]]
    })
  }

  syncJson() {
    const spec = this.buildSpec()
    const json = JSON.stringify(spec)
    if (this.hasHiddenJsonTarget) this.hiddenJsonTarget.value = json
    if (this.hasJsonPreviewTarget) this.jsonPreviewTarget.textContent = JSON.stringify(spec, null, 2)
    this.schedulePlacementPreview(spec)
  }

  schedulePlacementPreview(spec) {
    this._pendingPlacementSpec = spec
    if (this._placementTimer != null) return
    this._placementTimer = window.setTimeout(() => {
      this._placementTimer = null
      const pending = this._pendingPlacementSpec
      this._pendingPlacementSpec = null
      if (pending) this.emitPlacementPreview(pending)
    }, 0)
  }

  emitPlacementPreview(spec) {
    const fields = spec.fields || {}
    const sample = this.sampleDataValue[0] || {}
    application.controllers.forEach((ctrl) => {
      if (!PLACEMENT_PREVIEW_IDENTIFIERS.includes(ctrl.identifier)) return
      if (!this.element.contains(ctrl.element)) return
      if (typeof ctrl.applyPlacement !== "function") return
      ctrl.applyPlacement(fields, sample)
    })
  }

  enforcedTypeForKey(key, currentType) {
    const fixed = {
      email: "email",
      expiry: "date",
      status: "status"
    }
    const allowed = {
      id: ["text", "join"],
      email: ["email"],
      name: ["text", "join"],
      expiry: ["date"],
      status: ["status"]
    }
    const normalized = currentType === "string" ? "text" : currentType
    if (fixed[key]) return fixed[key]
    if (allowed[key]?.includes(normalized)) return normalized
    if (allowed[key]) return allowed[key][0]
    const customAllowed = ["text", "date", "map", "join"]
    return customAllowed.includes(normalized) ? normalized : "text"
  }

  buildSpec() {
    const spec = {
      reader_slug: this.hasReaderSlugTarget ? this.readerSlugTarget.value : "",
      fields: {}
    }

    this.fieldRowElements().forEach((row, idx) => {
      const key = this.val(row, "key")
      // `spec.fields` is a hash keyed by `field key`, so multiple blank rows would overwrite each other.
      // Use a unique placeholder for blank keys so the backend can validate each row.
      const fieldKey = key || `__blank_${idx}`

      const field = { type: this.enforcedTypeForKey(fieldKey, this.val(row, "type")) }
      const simpleTypes = ["text", "date", "email"]
      const mapRow = this.findDetailRow(row, "mapRow")
      const mapPathEl = mapRow?.querySelector("[data-field='path']")
      const simplePathEl = row.querySelector("[data-type-content='simple'] [data-field='path']")
      const joinPathEls = row.querySelectorAll("[data-type-content='join'] [data-field='path']")
      const joinInput = row.querySelector("[data-type-content='join'] [data-field='join']")
      const statusPathMainEl = row.querySelector("[data-type-content='status'] [data-field='path']")
      const mapPathMainEl = row.querySelector("[data-type-content='map'] [data-field='path']")

      if (field.type === "join") {
        const paths = Array.from(joinPathEls).map((s) => s.value).filter(Boolean)
        if (paths.length > 0) {
          field.path = paths
          if (joinInput?.value) field.join = joinInput.value
        }
      } else if (field.type === "status" && statusPathMainEl?.value) {
        field.path = statusPathMainEl.value
      } else if (field.type === "map" && (mapPathMainEl?.value || mapPathEl?.value)) {
        field.path = mapPathMainEl?.value || mapPathEl.value
      } else if (simpleTypes.includes(field.type) && simplePathEl?.value) {
        field.path = simplePathEl.value
      }

      const appleSlot = this.val(row, "apple_wallet_slot")
      const googleSlot = this.val(row, "google_wallet_slot")
      if (appleSlot) field.apple_wallet_slot = appleSlot
      if (googleSlot) field.google_wallet_slot = googleSlot

      const title = this.val(row, "title")
      if (title) field.title = title

      if (field.type === "status") {
        const rules = this.collectStatusRules(row)
        if (Object.keys(rules).length > 0) field.rules = rules
      }

      if (field.type === "map") {
        const rules = this.collectMapRules(row)
        if (Object.keys(rules).length > 0) field.rules = rules
      }

      spec.fields[fieldKey] = field
    })

    spec.value_filter = this.buildValueFilterPayload()
    return spec
  }

  buildValueFilterPayload() {
    const mode = this.valueFilterModeValue()
    return { mode, pairs: this.collectValueFilterPairs() }
  }

  valueFilterModeValue() {
    if (!this.hasValueFilterModeTarget) return "whitelist"
    const m = this.valueFilterModeTarget.value
    return m === "blacklist" ? "blacklist" : "whitelist"
  }

  collectValueFilterPairs() {
    if (!this.hasValueFilterPairsTarget) return []
    const rows = this.valueFilterPairsTarget.querySelectorAll("[data-value-filter-pair]")
    const out = []
    rows.forEach((row) => {
      const fk = row.querySelector("[data-value-filter-field-key]")?.value.trim() ?? ""
      const val = row.querySelector("[data-value-filter-value]")?.value.trim() ?? ""
      if (fk && val) out.push({ field_key: fk, value: val })
    })
    return out
  }

  addValueFilterPair() {
    if (!this.hasValueFilterPairsTarget) return
    this.valueFilterPairsTarget.appendChild(this.buildValueFilterPairRow())
    this.syncJson()
  }

  buildValueFilterPairRow() {
    const tr = document.createElement("tr")
    tr.dataset.valueFilterPair = ""
    tr.innerHTML = `
      <td>
        <input class="form-control form-control-sm" type="text" placeholder="e.g. type" autocomplete="off" value=""
          data-value-filter-field-key="" data-action="input->admin--field-mapping-editor#fieldChanged">
      </td>
      <td>
        <input class="form-control form-control-sm" type="text" placeholder="e.g. Surf Coach" autocomplete="off" value=""
          data-value-filter-value="" data-action="input->admin--field-mapping-editor#fieldChanged">
      </td>
      <td>
        <button class="btn btn-danger btn-sm" type="button" data-action="click->admin--field-mapping-editor#removeValueFilterPair" aria-label="Remove row">
          <i class="fas fa-times"></i>
        </button>
      </td>
    `
    return tr
  }

  removeValueFilterPair(event) {
    const row = event.currentTarget.closest("[data-value-filter-pair]")
    if (!row || !this.hasValueFilterPairsTarget) return
    const tbody = this.valueFilterPairsTarget
    const all = tbody.querySelectorAll("[data-value-filter-pair]")
    if (all.length <= 1) {
      row.querySelector("[data-value-filter-field-key]").value = ""
      row.querySelector("[data-value-filter-value]").value = ""
      this.syncJson()
      return
    }
    row.remove()
    this.syncJson()
  }

  fieldRowElements() {
    return this.hasFieldRowsTarget
      ? Array.from(this.fieldRowsTarget.querySelectorAll("[data-field-row]"))
      : []
  }

  val(row, name) {
    const el = row.querySelector(`[data-field='${name}']`)
    return el ? el.value.trim() : ""
  }

  collectStatusRules(fieldRow) {
    const detailRow = this.findDetailRow(fieldRow, "statusMapRow")
    if (!detailRow) return {}

    const rules = {}
    detailRow.querySelectorAll("[data-status-rule]").forEach((tr) => {
      const inputEl = tr.querySelector("[data-status-input]")
      const outputEl = tr.querySelector("[data-status-output]")
      const inputVal = inputEl?.value.trim()
      if (inputVal) rules[inputVal] = outputEl?.value || "Inactive"
    })
    return rules
  }

  collectMapRules(fieldRow) {
    const detailRow = this.findDetailRow(fieldRow, "mapRow")
    if (!detailRow) return {}

    const rules = {}
    detailRow.querySelectorAll("[data-map-rule]").forEach((tr) => {
      const inputEl = tr.querySelector("[data-map-input]")
      const outputEl = tr.querySelector("[data-map-output]")
      const inputVal = inputEl?.value.trim()
      if (inputVal) rules[inputVal] = outputEl?.value?.trim() || ""
    })
    return rules
  }

  findDetailRow(fieldRow, key) {
    let next = fieldRow.nextElementSibling
    while (next) {
      if (next.dataset[key] !== undefined) return next
      if (next.dataset.fieldRow !== undefined) break
      next = next.nextElementSibling
    }
    return null
  }

  updatePathDropdowns() {
    if (!this.sampleDataValue.length) return
    const sample = this.sampleDataValue[0]
    if (!sample) return

    const paths = this.extractPaths(sample, "")
    this.fieldRowElements().forEach(row => {
      const pathSelects = [...row.querySelectorAll("[data-field='path']")]
      const statusRow = this.findDetailRow(row, "statusMapRow")
      const mapRow = this.findDetailRow(row, "mapRow")
      if (statusRow) pathSelects.push(...statusRow.querySelectorAll("[data-field='path']"))
      if (mapRow) pathSelects.push(...mapRow.querySelectorAll("[data-field='path']"))

      pathSelects.forEach((select) => {
        const current = select.value
        if (select.tagName === "SELECT") {
          const fragment = document.createDocumentFragment()
          const blank = document.createElement("option")
          blank.value = ""
          blank.textContent = "— select —"
          fragment.appendChild(blank)
          paths.forEach((p) => {
            const opt = document.createElement("option")
            opt.value = p
            opt.textContent = this.inputFieldLabel(p)
            if (p === current) opt.selected = true
            fragment.appendChild(opt)
          })
          select.innerHTML = ""
          select.appendChild(fragment)
        }
      })
    })
  }

  extractPaths(obj, prefix) {
    let paths = []
    for (const [key, value] of Object.entries(obj)) {
      const fullPath = prefix ? `${prefix}.${key}` : key
      if (value && typeof value === "object" && !Array.isArray(value)) {
        paths = paths.concat(this.extractPaths(value, fullPath))
      } else {
        paths.push(fullPath)
      }
    }
    return paths
  }

  inputFieldLabel(path) {
    const cleaned = String(path || "")
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim()
      .toLowerCase()
    if (!cleaned) return ""
    return cleaned.charAt(0).toUpperCase() + cleaned.slice(1)
  }

  renderMappedPreview() {
    if (!this.hasMappedPreviewBodyTarget || !this.sampleDataValue.length) return

    const spec = this.buildSpec()
    const fields = spec.fields
    const keys = Object.keys(fields)
    if (!keys.length) return

    let html = ""
    this.sampleDataValue.slice(0, 5).forEach(item => {
      html += "<tr>"
      keys.forEach(key => {
        const fieldDef = fields[key]
        const raw = this.extractValue(item, fieldDef)
        html += `<td class="small">${this.escapeHtml(String(raw ?? ""))}</td>`
      })
      html += "</tr>"
    })

    const headerHtml = keys.map(k => `<th class="small">${this.escapeHtml(k)}</th>`).join("")
    const thead = this.mappedPreviewBodyTarget.closest("table").querySelector("thead")
    if (thead) thead.innerHTML = `<tr>${headerHtml}</tr>`

    this.mappedPreviewBodyTarget.innerHTML = html
  }

  extractValue(item, fieldDef) {
    if (!fieldDef || !fieldDef.path) return ""
    const paths = Array.isArray(fieldDef.path) ? fieldDef.path : [fieldDef.path]
    const values = paths.map((p) => this.getByPath(item, p))
    const raw = paths.length > 1 ? values.join(fieldDef.join || "") : values[0]
    if (fieldDef.type === "status" && fieldDef.rules) {
      const key = String(raw ?? "").trim()
      return fieldDef.rules[key] ?? fieldDef.rules[""] ?? raw ?? ""
    }
    if (fieldDef.type === "map" && fieldDef.rules) {
      const key = String(raw ?? "").trim()
      return fieldDef.rules[key] ?? fieldDef.rules[""] ?? raw ?? ""
    }
    return raw
  }

  getByPath(obj, path) {
    return path.split(".").reduce((o, k) => (o && o[k] !== undefined ? o[k] : null), obj)
  }

  escapeHtml(str) {
    const div = document.createElement("div")
    div.textContent = str
    return div.innerHTML
  }
}
